import React from 'react';
import {
  Box,
  Typography,
  TextField,
  FormControl,
  Select,
  MenuItem,
  Radio,
  RadioGroup,
  FormControlLabel,
} from '@mui/material';
import NumericFormatField from './NumericFormatField';
import { placeInputCursorAtStart } from '@/__core/utils/placeInputCursorAtStart';

interface LabeledFieldProps {
  label: string;
  children?: React.ReactNode;
  required?: boolean;
  error?: boolean;
  helperText?: string;
  labelWidth?: string | number;
  sx?: any;
  labelActions?: React.ReactNode;
  multiline?: boolean;
  autoSelect?: boolean; // New prop to enable/disable auto-select
  /** 'default' = do not override (e.g. react-number-format caret handling) */
  focusCursorPosition?: 'start' | 'default';
  fieldAlign?: 'center' | 'start';
  alignItems?: 'start' | 'center' | 'end';
  labelPosition?: 'top' | 'center' | 'bottom';
}

interface LabeledTextFieldProps extends Omit<React.ComponentProps<typeof TextField>, 'label'> {
  label: string;
  required?: boolean;
  error?: boolean;
  helperText?: string;
  labelWidth?: string | number;
  autoSelect?: boolean;
  alignItems?: 'start' | 'center' | 'end';
  labelPosition?: 'top' | 'center' | 'bottom';
  wrapperSx?: any;
}

interface LabeledSelectProps extends Omit<React.ComponentProps<typeof Select>, 'label'> {
  label: string;
  options: Array<{ id: string; name: string; [key: string]: any }>;
  placeholder?: string;
  required?: boolean;
  error?: boolean;
  helperText?: string;
  labelWidth?: string | number;
  autoSelect?: boolean;
  labelPosition?: 'top' | 'center' | 'bottom';
}

interface LabeledRadioGroupProps extends Omit<React.ComponentProps<typeof RadioGroup>, 'children'> {
  label: string;
  options: Array<{ value: string; label: React.ReactNode; disabled?: boolean }>;
  required?: boolean;
  error?: boolean;
  helperText?: string;
  labelWidth?: string | number;
  autoSelect?: boolean;
  formControlLabelSx?: any;
  wrapperSx?: any;
  labelPosition?: 'top' | 'center' | 'bottom';
}

// Base LabeledField component — places cursor at start on focus (or selects all when autoSelect)
export function LabeledField({ 
  label, 
  children, 
  required = false, 
  error = false, 
  helperText,
  labelWidth = "150px",
  sx,
  labelActions,
  multiline = false,
  autoSelect = false,
  focusCursorPosition = 'start',
  fieldAlign = 'center',
  alignItems,
  labelPosition = 'center',
}: LabeledFieldProps) {
  const alignItemsValue =
    alignItems === 'start'
      ? 'flex-start'
      : alignItems === 'end'
      ? 'flex-end'
      : alignItems === 'center'
      ? 'center'
      : undefined;

  const verticalAlign =
    alignItemsValue ||
    (labelPosition === 'top'
      ? 'flex-start'
      : labelPosition === 'bottom'
      ? 'flex-end'
      : labelPosition === 'center'
      ? 'center'
      : multiline || fieldAlign === 'start'
      ? 'flex-start'
      : 'center');

  const isCenterAligned = verticalAlign === 'center';

  const handleInputFocusCapture = (event: React.FocusEvent) => {
    const target = event.target as HTMLElement;
    if (target.tagName !== 'INPUT' && target.tagName !== 'TEXTAREA') {
      return;
    }

    if (focusCursorPosition === 'default') {
      return;
    }

    placeInputCursorAtStart(event as React.FocusEvent<HTMLInputElement | HTMLTextAreaElement>, {
      autoSelect,
    });
  };

  const shouldShowLabel = labelWidth !== 0 && label !== '';
  const resolvedLabelWidth = typeof labelWidth === 'number' ? `${labelWidth}px` : labelWidth;

  return (
    // Keep a consistent two-column layout and allow explicit vertical alignment control.
    <Box
      sx={{ 
        mb: 1,
        display: "flex", 
        alignItems: verticalAlign,
        minHeight: isCenterAligned ? '25px' : 'auto',
        gap: 2,
        position: "relative",
        ...sx 
      }}
    >
      {shouldShowLabel && (
      <Box sx={{ 
        display: 'flex', 
        alignItems: verticalAlign,
        alignSelf: verticalAlign,
        minHeight: isCenterAligned ? '25px' : 'auto',
        gap: 1, 
        width: resolvedLabelWidth,
        minWidth: resolvedLabelWidth,
        maxWidth: resolvedLabelWidth,
        pt: verticalAlign === 'flex-start' && multiline ? 0.5 : 0,
      }}>
        <Typography
          variant="body2"
          sx={{
            fontWeight: 400,
            color: "#374151",
            lineHeight: '1.2',
            whiteSpace: 'nowrap'
          }}
        >
          {label}
          {required && <span style={{ color: '#ef4444' }}> *</span>}
        </Typography>
        {labelActions && (
          <Box>
            {labelActions}
          </Box>
        )}
      </Box>
      )}
      <Box 
        sx={{
          flex: 1,
          position: 'relative',
          display: 'flex',
          flexDirection: verticalAlign === 'flex-start' ? 'column' : 'row',
          alignItems: verticalAlign === 'flex-start' ? 'stretch' : verticalAlign,
          minWidth: 0,
          minHeight: isCenterAligned ? '25px' : 'auto',
        }}
        onFocusCapture={handleInputFocusCapture}
      >
        {children}
        {error && helperText && (
          <Typography
            variant="caption"
            sx={{
              color: '#ef4444',
              mt: 0.5,
              display: 'block',
            }}
          >
            {helperText}
          </Typography>
        )}
      </Box>
    
    </Box>
  );
}

// LabeledTextField component
export function LabeledTextField({
  label,
  required = false,
  error = false,
  helperText,
  labelWidth,
  size = "small",
  fullWidth = true,
  sx,
  wrapperSx,
  multiline,
  autoSelect = false,
  alignItems,
  labelPosition = 'center',
  ...textFieldProps
}: LabeledTextFieldProps) {
  return (
    <LabeledField
      label={label}
      required={required}
      error={error}
      helperText={helperText}
      labelWidth={labelWidth}
      multiline={multiline}
      autoSelect={autoSelect}
      alignItems={alignItems}
      labelPosition={labelPosition}
      sx={wrapperSx}
    >
      <TextField
        size={size}
        fullWidth={fullWidth}
        error={error}
        multiline={multiline}
        sx={{
          "& .MuiOutlinedInput-root": {
            "& fieldset": { borderColor: "#e5e7eb" },
            "&:hover fieldset": { borderColor: "none" },
            "&.Mui-focused fieldset": {
              borderColor: "#3b82f6",
            },
          },
          "& .MuiInputBase-input": {
            fontWeight: 400,
            color: "#374151",
          },
          "& .MuiInputBase-input.Mui-disabled": {
            WebkitTextFillColor: "#374151",
            color: "#374151",
            opacity: 1,
          },
          ...sx,
        }}
        {...textFieldProps}
      />
    </LabeledField>
  );
}

// LabeledSelect component
export function LabeledSelect({
  label,
  options,
  placeholder = "Chọn...",
  required = false,
  error = false,
  helperText,
  labelWidth,
  size = "small",
  fullWidth = true,
  sx,
  autoSelect = false, // Select doesn't need auto-select
  labelPosition = 'center',
  ...selectProps
}: LabeledSelectProps) {
  // Ensure options is always an array
  const safeOptions = Array.isArray(options) ? options : [];
  
  return (
    <LabeledField
      label={label}
      required={required}
      error={error}
      helperText={helperText}
      labelWidth={labelWidth}
      autoSelect={autoSelect}
      labelPosition={labelPosition}
    >
      <FormControl size={size} fullWidth={fullWidth} error={error}>
        <Select
          displayEmpty
          value={selectProps.value && safeOptions.length > 0 && safeOptions.some(option => option.id === selectProps.value) ? selectProps.value : ''}
          renderValue={(value) => {
            if (value === '') {
              return '';
            }
            const selectedOption = safeOptions.find(option => option.id === value);
            return selectedOption ? selectedOption.name : '';
          }}
          sx={{
            "& .MuiOutlinedInput-notchedOutline": {
              borderColor: "#e5e7eb",
            },
            "&:hover .MuiOutlinedInput-notchedOutline": {
              borderColor: "#d1d5db",
            },
            "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
              borderColor: "#3b82f6",
            },
            "& .MuiSelect-select": {
              fontWeight: 400,
              color: "#374151",
            },
            "& .MuiSelect-select.Mui-disabled": {
              WebkitTextFillColor: "#374151",
              color: "#374151",
              opacity: 1,
            },
            ...sx,
          }}
          {...selectProps}
        >
          {safeOptions.map((option) => (
            <MenuItem key={option.id} value={option.id}>
              {option.name}
            </MenuItem>
          ))}
        </Select>
      </FormControl>
    </LabeledField>
  );
}

export function LabeledRadioGroup({
  label,
  options,
  required = false,
  error = false,
  helperText,
  labelWidth,
  autoSelect = false,
  formControlLabelSx,
  wrapperSx,
  labelPosition = 'center',
  ...radioGroupProps
}: LabeledRadioGroupProps) {
  const safeOptions = Array.isArray(options) ? options : [];

  return (
    <LabeledField
      label={label}
      required={required}
      error={error}
      helperText={helperText}
      labelWidth={labelWidth}
      autoSelect={autoSelect}
      labelPosition={labelPosition}
      fieldAlign={labelPosition === 'center' ? 'center' : 'start'}
      sx={{ minHeight: '25px', ...wrapperSx }}
    >
      <FormControl error={error} fullWidth>
        <RadioGroup {...radioGroupProps}>
          {safeOptions.map((option) => (
            <FormControlLabel
              key={option.value}
              value={option.value}
              control={<Radio />}
              label={option.label}
              disabled={option.disabled}
              sx={{
                '& .MuiFormControlLabel-label': {
                  fontWeight: 400,
                  color: '#374151',
                },
                '& .MuiRadio-root': {
                  color: '#374151',
                },
                '& .MuiCheckbox-root': {
                  color: '#374151',
                },
                ...formControlLabelSx,
              }}
            />
          ))}
        </RadioGroup>
      </FormControl>
    </LabeledField>
  );
}

// HOC for custom components
export function withLabeledField<P extends object>(
  WrappedComponent: React.ComponentType<P>
) {
  return React.forwardRef<HTMLDivElement, P & LabeledFieldProps>(
    (props, ref) => {
      const { label, required, error, helperText, sx, ...restProps } = props;
      
      return (
        <LabeledField
          label={label}
          required={required}
          error={error}
          helperText={helperText}
          sx={sx}
        >
          <WrappedComponent
            ref={ref}
            {...(restProps as P)}
          />
        </LabeledField>
      );
    }
  );
}

// LabeledNumericField component for numeric display
interface LabeledNumericFieldProps {
  label: string;
  value: string | number;
  required?: boolean;
  error?: boolean;
  helperText?: string;
  labelWidth?: string | number;
  decimalScale?: number;
  thousandSeparator?: string;
  decimalSeparator?: string;
  displayType?: 'text' | 'input';
  renderText?: (value: string) => React.ReactNode;
  onValueChange?: (values: { value: string; floatValue: number | undefined }) => void;
  onChange?: (event: React.ChangeEvent<HTMLInputElement>) => void;
  size?: 'small' | 'medium';
  fullWidth?: boolean;
  textAlign?: 'left' | 'center' | 'right';
  disabled?: boolean;
  placeholder?: string;
  labelActions?: React.ReactNode; // New prop for label actions
  delete?: boolean; // New prop for delete functionality
  onDelete?: () => void; // Callback for delete action
}

export function LabeledNumericField({
  label,
  value,
  required = false,
  error = false,
  helperText,
  labelWidth = "150px",
  decimalScale = 0,
  thousandSeparator = ".",
  decimalSeparator = ",",
  displayType = "text",
  renderText,
  onValueChange,
  onChange,
  size = "small",
  fullWidth = true,
  textAlign = "right",
  disabled = false,
  placeholder,
  labelActions,
}: LabeledNumericFieldProps) {
  return (
    <LabeledField
      label={label}
      required={required}
      error={error}
      helperText={helperText}
      labelWidth={labelWidth}
      labelActions={labelActions}
      focusCursorPosition="default"
    >
      <NumericFormatField
        value={value}
        onValueChange={onValueChange}
        onChange={onChange}
        decimalScale={decimalScale}
        thousandSeparator={thousandSeparator}
        decimalSeparator={decimalSeparator}
        displayType={displayType}
        renderText={renderText}
        size={size}
        fullWidth={fullWidth}
        textAlign={textAlign}
        disabled={disabled}
        placeholder={placeholder}
        error={error}
      
      />
    
    </LabeledField>
  );
}

export default LabeledField;
